import type {
  ConditionFieldDescription,
  OutputReference,
} from "@smartsuite/automation-engine-api";
import type { CandidateStore } from "../../../../../../../stores/automation/candidate.store";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../types/candidate";

// eslint-disable-next-line import/no-unused-modules
export interface ReferencedConditionField {
  sourceEntity: TriggerCandidate | ActionCandidate;
  fieldDescription: ConditionFieldDescription;
}

export const resolveReferencedConditionField = (
  candidate: CandidateStore,
  outputReference: OutputReference | undefined,
): ReferencedConditionField | undefined => {
  if (outputReference === undefined) return undefined;

  const source = outputReference.source;
  let sourceEntity: TriggerCandidate | ActionCandidate | undefined;

  if (source.oneofKind === "triggerReference") {
    const trigger = candidate.self.trigger;
    // BUG-21229: after a trigger change, a stale reference to the OLD trigger
    // can still find an overlapping conditionFieldId on the new trigger and
    // silently resolve -- require the stored triggerId to match the current
    // trigger (mirrors checkActionInput's trigger-reference check).
    if (
      source.triggerReference.triggerId !== trigger.triggerReference?.triggerId
    ) {
      return undefined;
    }
    sourceEntity = trigger;
  } else if (source.oneofKind === "actionReference") {
    const { instanceId, actionId } = source.actionReference;
    // Changing an action's type keeps its instanceId, so match actionId too.
    sourceEntity = candidate.allActions.find(
      (action) =>
        action.actionReference?.instanceId === instanceId &&
        action.actionReference.actionId === actionId,
    );
  } else {
    // loopItem is stored as a copied trigger/action reference on the left,
    // so an undefined-kind source has no resolvable entity.
    return undefined;
  }

  if (sourceEntity === undefined) return undefined;

  const fieldDescription = sourceEntity.description.conditionFields.find(
    (candidateField) =>
      candidateField.conditionFieldId === outputReference.outputId,
  );
  if (fieldDescription === undefined) return undefined;

  return { sourceEntity, fieldDescription };
};
